import { useId, useMemo } from "react";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { FieldComponentProps } from "~/lib/form";
import { cn } from "~/shared/lib/utils";

interface ValidatedPhoneInputProps extends FieldComponentProps {
  countryCode?: string;
  description?: string;
  disabled?: boolean;
  required?: boolean;
}

const DEFAULT_COUNTRY_CODE = "+1";

export function ValidatedPhoneInput(props: ValidatedPhoneInputProps) {
  const {
    field,
    label,
    placeholder = "(555) 123-4567",
    className,
    countryCode = DEFAULT_COUNTRY_CODE,
    description,
    disabled,
    required,
  } = props;

  const inputId = `${field.name}-${useId()}-phone-input`;
  const meta = field.state.meta;
  const normalizedDigits = useMemo(
    () => extractDigits(field.state.value as string | null, countryCode),
    [field.state.value, countryCode],
  );
  const formattedValue = useMemo(
    () => formatForDisplay(normalizedDigits),
    [normalizedDigits],
  );

  return (
    <div className={cn("space-y-2", className)}>
      <Label htmlFor={inputId}>{label}</Label>
      <Input
        id={inputId}
        name={field.name}
        type="tel"
        inputMode="tel"
        autoComplete="tel"
        value={formattedValue}
        placeholder={placeholder}
        onBlur={field.handleBlur}
        onChange={(event) => {
          const digits = event.target.value.replace(/\D/g, "");
          const canonical =
            digits.length === 0 ? "" : ensureCountryCode(digits, countryCode);
          field.handleChange(canonical);
        }}
        disabled={field.form.state.isSubmitting || disabled}
        aria-invalid={!!meta.errors.length}
        aria-describedby={meta.errors.length ? `${inputId}-errors` : undefined}
        required={required}
      />
      {description && <p className="text-muted-foreground text-sm">{description}</p>}
      {meta.isTouched && meta.errors.length > 0 && (
        <div
          id={`${inputId}-errors`}
          role="alert"
          aria-live="polite"
          className="text-destructive text-sm font-medium"
        >
          {label}: {meta.errors.join(", ")}
        </div>
      )}
    </div>
  );
}

function extractDigits(value: string | null | undefined, countryCode: string) {
  return normalizeDigits(value, countryCode);
}

function ensureCountryCode(digits: string, countryCode: string) {
  if (!digits) return "";
  const trimmed = normalizeDigits(digits, countryCode);
  return `${countryCode}${trimmed}`;
}

function normalizeDigits(value: string | null | undefined, countryCode: string) {
  if (!value) return "";
  const digitsOnly = value.replace(/\D/g, "");
  const normalizedCountry = countryCode.replace(/\D/g, "");
  const stripped = digitsOnly.startsWith(normalizedCountry)
    ? digitsOnly.slice(normalizedCountry.length)
    : digitsOnly;
  return stripped.slice(0, 10);
}

function formatForDisplay(digits: string) {
  if (!digits) return "";
  const part1 = digits.slice(0, 3);
  const part2 = digits.slice(3, 6);
  const part3 = digits.slice(6, 10);

  if (digits.length <= 3) {
    return `(${part1}`;
  }

  if (digits.length <= 6) {
    return `(${part1}) ${part2}`;
  }

  return `(${part1}) ${part2}-${part3}`;
}
